import { Component, effect, inject, Injector, input, OnInit, signal, untracked } from '@angular/core';
import { Validators } from '@angular/forms';

import { ButtonModule, DialogRef, DialogService, IconModule } from '@hglobal/fin-ui';
import { FormBuilder, FormControl } from '@ngneat/reactive-forms';
import { TranslateModule } from '@ngx-translate/core';
import { v4 as uuid } from 'uuid';

import { ExchangeFailedComponent, ExchangeSuccessComponent } from '@kr/components/exchange/components';
import { CryptoFiatDetailsComponent } from '@kr/components/exchange/components/crypto-fiat-details/crypto-fiat-details.component';
import { ExchangeSetCryptoFiatAmountComponent } from '@kr/components/exchange/components/exchange-set-crypto-fiat-amount/exchange-set-crypto-fiat-amount.component';
import {
  ExchangeSetCryptoFiatIbanComponent,
  pattern,
} from '@kr/components/exchange/components/exchange-set-crypto-fiat-iban/exchange-set-crypto-fiat-iban.component';
import { ExchangeStore } from '@kr/components/exchange/exchange.store';
import { Step, StepperComponent } from '@kr/components/stepper/stepper.component';
import { WalletCard } from '@kr/models';

enum ExchangeCryptoFiatFlow {
  SetAmount = 1,
  SetIban = 2,
  Details = 3,
  Success = 4,
  Failed = 5,
}

@Component({
  selector: 'kr-crypto-fiat-flow',
  imports: [
    ExchangeSetCryptoFiatAmountComponent,
    ExchangeSetCryptoFiatIbanComponent,
    ButtonModule,
    TranslateModule,
    StepperComponent,
    CryptoFiatDetailsComponent,
    ExchangeFailedComponent,
    ExchangeSuccessComponent,
    IconModule,
  ],
  templateUrl: './crypto-fiat-flow.component.html',
  styleUrl: './crypto-fiat-flow.component.scss',
})
export class CryptoFiatFlowComponent implements OnInit {
  readonly #fb = inject(FormBuilder);
  readonly #store = inject(ExchangeStore);
  readonly #dialogService = inject(DialogService);
  readonly #injector = inject(Injector);

  protected readonly Number = Number;

  dialogRef = inject(DialogRef);
  wallet = input<WalletCard>();
  wallets = input<WalletCard[]>();

  timeLeft = signal(0);
  estimation = this.#store.exchangeEstimate;

  form: any;
  exchange = ExchangeCryptoFiatFlow;
  page = signal<number>(this.exchange.SetAmount);

  steps: Step[] = [
    {
      value: 1,
      completed: false,
    },
    {
      value: 2,
      completed: false,
    },
    {
      value: 3,
      completed: false,
    },
  ];

  constructor() {
    effect(() => {
      this.steps.map((step, i) => {
        if (i < this.page() - 1) {
          step.completed = true;
        }
      });
    });

    effect(() => {
      const isSuccess = this.#store.isExchangeSuccess();
      const isFail = this.#store.exchangeError();
      const isPageOpened = location.pathname.includes('exchange');

      if (isSuccess) {
        untracked(() => {
          if (isPageOpened) {
            this.page.set(this.exchange.SetAmount);
            this.#dialogService.open(ExchangeSuccessComponent, null, { parentInjector: this.#injector });
          } else {
            this.page.set(this.exchange.Success);
          }
        });
      }

      if (isFail) {
        untracked(() => {
          if (isPageOpened) {
            this.page.set(this.exchange.SetAmount);
            this.#dialogService.open(ExchangeFailedComponent, null, { parentInjector: this.#injector });
          } else {
            this.page.set(this.exchange.Failed);
          }
        });
      }
    });
  }

  ngOnInit(): void {
    this.form = this.#fb.group<any>(
      {
        transferType: new FormControl('sepa', { updateOn: 'change' }),
        amount: null,
        amountReceive: null,
        walletToId: null,
        walletFromId: [this.wallet().id, Validators.required],
        comment: [null, [Validators.pattern(pattern)]],
        BICOrSWIFTCode: null,
        beneficiaryAddress: null,
        beneficiaryBankAddress: null,
        beneficiaryBankName: null,
        beneficiaryName: null,
        correspondentBankBICOrSWIFTCode: null,
        correspondentBankAddress: null,
        correspondentBankName: null,
        currency: 'EUR',
        iban: null,
        incorporateNum: null,
        reference: null,
        saveAccountDetails: false,
      },
      { updateOn: 'blur' },
    );

    this.#store.saveForm(this.form);
    this.#store.saveWallets(this.wallets);
  }

  getStep(i: number): Step {
    return { value: i, completed: false };
  }

  setStep(i: number): void {
    const isFormInValid = this.form.invalid;
    const amount = Number(this.form.controls.amount.value?.replaceAll(',', ''));
    const amountReceive = Number(this.form.controls.amountReceive.value?.replaceAll(',', ''));

    if (isFormInValid || !(amount > 0 || amountReceive > 0)) {
      return;
    }

    this.page.set(i);
  }

  execute() {
    this.#store.sendExchange({
      estimationId: this.estimation().estimationId,
      requestId: uuid(),
    });
  }
}
